import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText } from "lucide-react";
import { LegalFooter } from "./privacy-policy";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LAST_UPDATED = "26 September 2026";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href={`${BASE}/`}>
            <span className="font-bold text-lg text-primary cursor-pointer">RetailPulse</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href={`${BASE}/login`}><Button variant="ghost" size="sm">Sign in</Button></Link>
            <Link href={`${BASE}/signup`}><Button size="sm">Get started free</Button></Link>
          </div>
        </div>
      </header>

      <section className="border-b bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
            <FileText className="w-3 h-3 mr-1" /> Legal
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Terms of Service</h1>
          <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <div className="space-y-10 text-sm leading-relaxed text-foreground">

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">1. Agreement to terms</h2>
            <p className="text-muted-foreground">
              By creating an account or using RetailPulse ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Service. These Terms form a binding agreement between you ("Customer" or "you") and RetailPulse.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">2. The service</h2>
            <p className="text-muted-foreground">
              RetailPulse provides a cloud-based point-of-sale, inventory management, and business analytics platform accessible via web browser. We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">3. Account registration</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>You must be at least 18 years old and capable of entering a legally binding contract under Nigerian law.</li>
              <li>You must provide accurate, current, and complete information during registration.</li>
              <li>You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.</li>
              <li>You must notify us immediately at <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a> if you suspect unauthorised access to your account.</li>
              <li>Each store account is limited to one registered business. Creating multiple accounts to circumvent subscription limits is prohibited.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">4. Free trial & subscription</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>New accounts receive a <strong className="text-foreground">10-day free trial</strong> with full access to all features on your selected plan.</li>
              <li>After the trial, a paid subscription is required to continue using the Service. Unpaid accounts will lose access to protected features.</li>
              <li>Subscriptions are billed monthly via Paystack. Plans: Basic (₦7,500/mo), Pro (₦12,000/mo), Enterprise (₦28,000/mo).</li>
              <li>Prices are in Nigerian Naira (₦) and are inclusive of applicable VAT where required.</li>
              <li>We reserve the right to change pricing with 30 days' notice. Existing subscribers will be notified before any price change takes effect.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">5. Acceptable use</h2>
            <p className="text-muted-foreground">You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Violate any Nigerian federal or state law or regulation.</li>
              <li>Process transactions for illegal goods or services.</li>
              <li>Upload, store, or transmit malicious code, viruses, or harmful data.</li>
              <li>Attempt to gain unauthorised access to our systems or another customer's data.</li>
              <li>Reverse-engineer, decompile, or resell the Service without written permission.</li>
              <li>Use the Service to harass, defraud, or harm any person.</li>
            </ul>
            <p className="text-muted-foreground">
              We reserve the right to suspend or terminate accounts that violate these terms without refund.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">6. Your data</h2>
            <p className="text-muted-foreground">
              You retain full ownership of all business data you enter into RetailPulse (products, sales, customers, etc.). You grant us a limited licence to store and process this data solely to provide the Service. See our <Link href={`${BASE}/privacy-policy`}><span className="text-primary underline cursor-pointer">Privacy Policy</span></Link> for full details.
            </p>
            <p className="text-muted-foreground">
              You are responsible for the accuracy and legality of data you enter, including customer personal information and pricing records.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">7. Intellectual property</h2>
            <p className="text-muted-foreground">
              The RetailPulse platform, brand, design, code, and all associated materials are the intellectual property of RetailPulse. Nothing in these Terms transfers ownership of any intellectual property to you. You may not use our name, logo, or trademarks without written consent.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">8. Uptime & availability</h2>
            <p className="text-muted-foreground">
              We aim for high availability but do not guarantee uninterrupted access. Scheduled maintenance, third-party outages, or circumstances beyond our control may cause temporary downtime. We will provide reasonable advance notice of planned maintenance where possible.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">9. Limitation of liability</h2>
            <p className="text-muted-foreground">
              To the maximum extent permitted by Nigerian law, RetailPulse shall not be liable for any indirect, incidental, special, or consequential damages — including loss of profit, loss of data, or business interruption — arising from your use of or inability to use the Service. Our total liability to you for any claim shall not exceed the amount you paid us in the 3 months preceding the claim.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">10. Termination</h2>
            <p className="text-muted-foreground">
              You may request cancellation at any time by emailing{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a> or contacting us via WhatsApp. Cancellation takes effect at the end of your current paid period — you will retain access until then. We may terminate your account immediately for material breach of these Terms.
            </p>
            <p className="text-muted-foreground">
              For 30 days after your cancellation takes effect, you may request and receive an export of your business data by contacting support. After that period, we delete your account and business data, except records we must retain under the law. See our <Link href={`${BASE}/privacy-policy`}><span className="text-primary underline cursor-pointer">Privacy Policy</span></Link> for data retention details and our <Link href={`${BASE}/refund-policy`}><span className="text-primary underline cursor-pointer">Refund Policy</span></Link> for refund eligibility.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">11. Governing law</h2>
            <p className="text-muted-foreground">
              These Terms are governed by the laws of the Federal Republic of Nigeria. Any dispute shall be subject to the exclusive jurisdiction of Nigerian courts.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">12. Changes to these terms</h2>
            <p className="text-muted-foreground">
              We may update these Terms from time to time. Material changes will be communicated via email or in-app notice at least 14 days before they take effect. Continued use of the Service after changes take effect constitutes acceptance.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">13. Contact</h2>
            <p className="text-muted-foreground">
              Questions about these Terms?{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a>
              {" "}or WhatsApp{" "}
              <a href="https://wa.me/2349040460813" target="_blank" rel="noopener noreferrer" className="text-primary underline">+234 904 046 0813</a>.
            </p>
          </section>

        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
